import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Award, Calculator, Download, FileBadge, Loader2, Ban } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import {
  awardTicket, certificateDownloadUrl, issueCertificate, listCertificates, listNominations,
  nominationResults, revokeCertificate, revokeTicket, tallyNomination, ticketHistory,
  type Certificate, type Nomination, type NominationResult, type Ticket,
} from './api'
import { NominationStatusChip, seatLabel } from './vocab'

/**
 * Who won, and the candidacy the ticket created.
 *
 * ## Awarding is the loop closing
 *
 * A ticket is not a label on a winner — it writes an `election_candidacy` row with the party
 * copied at nomination, which is the row the Vote Tallying Centre counts against on election
 * night. That is why nominations and tallying are one system rather than two: the output of
 * this page is the input of that one.
 *
 * ## Ties are never resolved automatically
 *
 * A tied contest stays tied and says so. Breaking it is somebody's recorded decision, not an
 * ordering artefact of whichever row the database returned first.
 */
export function ResultsPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const nominationId = params.get('nomination')

  const [contests, setContests] = useState<Nomination[]>([])
  const [results, setResults] = useState<NominationResult[] | null>(null)
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    if (!electionId) return
    void (async () => {
      try {
        setContests(await listNominations({ electionId, size: 300 }))
      } catch (err) {
        setError(explain(err))
      }
    })()
  }, [electionId])

  const load = useCallback(async () => {
    if (!nominationId) { setResults(null); setTickets([]); setCertificates([]); return }
    try {
      setError(null)
      const [r, t, c] = await Promise.all([
        nominationResults(nominationId), ticketHistory(nominationId),
        listCertificates(nominationId).catch(() => [] as Certificate[]),
      ])
      setResults(r)
      setTickets(t)
      setCertificates(c)
    } catch (err) {
      setError(explain(err))
      setResults([])
    }
  }, [nominationId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A nomination result" />

  const contest = contests.find((c) => c.id === nominationId)
  const liveTicket = tickets.find((t) => t.revokedAt === null)

  const certify = async (what: 'issue' | 'download' | 'revoke', certificateId?: string) => {
    if (!nominationId) return
    setBusy(what)
    setError(null)
    try {
      if (what === 'issue') await issueCertificate(nominationId)
      else if (what === 'download' && certificateId) {
        const link = await certificateDownloadUrl(nominationId, certificateId)
        // A presigned URL straight to the object store — the PDF never passes through the
        // API, and the link expires in ten minutes.
        window.open(link.url, '_blank', 'noopener')
        return
      } else if (what === 'revoke' && certificateId) {
        const reason = window.prompt(
          'Reason for withdrawing this certificate (the holder of the printed copy will be '
          + 'shown it when they verify):')
        if (!reason) return
        await revokeCertificate(nominationId, certificateId, reason)
      }
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const run = async (what: 'tally' | 'award' | 'revoke', aspirantId?: string) => {
    if (!nominationId) return
    if (what === 'award' && !window.confirm(
      'Awarding the ticket creates this aspirant\'s candidacy for the general election. '
      + 'Continue?')) return
    setBusy(what)
    setError(null)
    try {
      if (what === 'tally') await tallyNomination(nominationId)
      else if (what === 'award' && aspirantId) await awardTicket(nominationId, aspirantId)
      else if (what === 'revoke') {
        const reason = window.prompt('Reason for revoking the ticket (recorded permanently):')
        if (!reason) return
        await revokeTicket(nominationId, reason)
      }
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Results &amp; tickets</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Count the ballots, then award the party's ticket — which creates the candidacy.
          </p>
        </div>
        {contest && (
          <div className="flex items-center gap-2">
            <NominationStatusChip code={contest.statusCode} />
            {(contest.statusCode === 'CLOSED' || contest.statusCode === 'TALLIED') && (
              <button type="button" onClick={() => void run('tally')}
                className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
                {busy === 'tally' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Calculator className="h-3.5 w-3.5" />}
                Count
              </button>
            )}
          </div>
        )}
      </div>

      <label className="block max-w-xl">
        <span className="block text-erp-xs text-muted-foreground mb-1">Contest</span>
        <select value={nominationId ?? ''} className="erp-input h-9 w-full text-erp-sm"
          onChange={(e) => setParams((p) => {
            if (e.target.value) p.set('nomination', e.target.value)
            else p.delete('nomination')
            return p
          }, { replace: true })}>
          <option value="">Choose a contest…</option>
          {contests.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title} · {seatLabel(c.seatTypeCode)} · {c.partyShortName ?? ''}
            </option>
          ))}
        </select>
      </label>

      <ErrorStrip message={error} className="rounded-md border" />

      {/*
          The chain from ballots to certificate is four separate acts, and each button only
          appears once its predecessor has happened. That is the right behaviour and a
          terrible experience on its own: a contest still in VOTING shows an empty table and
          NO controls, which reads as broken rather than as "not yet". So the page says which
          step is next and where it lives.
      */}
      {contest && <NextStep contest={contest} hasResults={(results?.length ?? 0) > 0}
        hasTicket={liveTicket !== undefined}
        hasCertificate={certificates.some((c) => c.live)} />}

      {!nominationId && (
        <EmptyState title="Choose a contest" hint="Results belong to one contest." />
      )}

      {nominationId && results !== null && results.length === 0 && !error && (
        <EmptyState title="Not counted yet"
          hint="Close voting, then count. The tally is one grouped query and writes the
                results once." />
      )}

      {nominationId && (results === null || results.length > 0) && (
        <ErpTable head={(
          <>
            <Th className="w-12 text-right">#</Th>
            <Th>Aspirant</Th>
            <Th className="text-right">Votes</Th>
            <Th className="text-right">Share</Th>
            <Th>Outcome</Th>
            <Th />
          </>
        )}>
          {results === null ? <LoadingRows colSpan={6} />
            : results.map((r) => (
              <tr key={r.aspirantId}>
                <Td className="text-right tabular-nums text-muted-foreground">{r.position}</Td>
                <Td className="font-medium">{r.ballotName ?? '—'}</Td>
                <Td className="text-right tabular-nums font-medium">{fmtNumber(r.votes)}</Td>
                <Td className="text-right tabular-nums">
                  {r.sharePct == null ? '—' : `${r.sharePct}%`}
                </Td>
                <Td>
                  <span className={cn('text-erp-xs',
                    r.outcomeCode === 'WON' && 'text-emerald-700 dark:text-emerald-400 font-medium',
                    r.outcomeCode === 'TIED' && 'text-amber-700 dark:text-amber-400 font-medium')}>
                    {r.outcomeCode.toLowerCase()}
                  </span>
                </Td>
                <Td className="text-right">
                  {r.outcomeCode === 'WON' && !liveTicket && (
                    <button type="button" disabled={busy !== null}
                      onClick={() => void run('award', r.aspirantId)}
                      className="inline-flex items-center gap-1 text-erp-xs text-chocolate
                                 hover:underline">
                      <Award className="h-3 w-3" /> Award ticket
                    </button>
                  )}
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      {results?.some((r) => r.outcomeCode === 'TIED') && (
        <Note tone="bad">
          This contest is tied. It is deliberately not broken automatically — a tie is
          somebody's recorded decision, not an artefact of which row came back first.
        </Note>
      )}

      {tickets.length > 0 && (
        <div>
          <h3 className="text-erp-sm font-medium mb-1.5">Tickets</h3>
          <ErpTable head={(
            <>
              <Th>Aspirant</Th>
              <Th>Awarded</Th>
              <Th>Candidacy</Th>
              <Th>Revoked</Th>
              <Th />
            </>
          )}>
            {tickets.map((t) => (
              <tr key={t.id}>
                <Td className="font-medium">{t.ballotName ?? '—'}</Td>
                <Td className="text-erp-xs">{fmtDateTime(t.awardedAt)}</Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {t.candidacyId ? 'created' : '—'}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {t.revokedAt ? `${fmtDateTime(t.revokedAt)} · ${t.revokeReason ?? ''}` : '—'}
                </Td>
                <Td className="text-right">
                  {t.revokedAt === null && (
                    <button type="button" disabled={busy !== null}
                      onClick={() => void run('revoke')}
                      className="text-erp-xs text-red-700 dark:text-red-400 hover:underline">
                      Revoke
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>
          <Note tone="info">
            Awarding created an <strong>election candidacy</strong> with the party as it stood
            on nomination day. That is the row the Vote Tallying Centre counts against — so a
            ticket here becomes a name on a Form 34A there.
          </Note>
        </div>
      )}

      {/* ── certificates ──────────────────────────────────────────────── */}
      {liveTicket && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
            <h3 className="text-erp-sm font-medium">Certificates</h3>
            {!certificates.some((c) => c.live) && (
              <button type="button" disabled={busy !== null}
                onClick={() => void certify('issue')}
                className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
                {busy === 'issue' ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <FileBadge className="h-3 w-3" />}
                Issue certificate
              </button>
            )}
          </div>

          {certificates.length === 0 ? (
            <p className="text-erp-xs text-muted-foreground">
              None issued yet. The certificate is the document the candidate presents to the
              electoral commission.
            </p>
          ) : (
            <ErpTable head={(
              <>
                <Th>Reference</Th>
                <Th>Issued</Th>
                <Th>Status</Th>
                <Th />
              </>
            )}>
              {certificates.map((c) => (
                <tr key={c.id}>
                  <Td className="font-mono text-erp-xs">{c.reference}</Td>
                  <Td className="text-erp-xs">{fmtDateTime(c.issuedAt)}</Td>
                  <Td>
                    {c.live ? (
                      <span className="text-erp-xs text-emerald-700 dark:text-emerald-400">
                        valid
                      </span>
                    ) : (
                      <span className="text-erp-xs text-amber-700 dark:text-amber-400">
                        withdrawn{c.revokeReason ? ` — ${c.revokeReason}` : ''}
                      </span>
                    )}
                    {c.fileId === null && (
                      <span className="block text-erp-xs text-red-700 dark:text-red-400">
                        PDF missing — reissue
                      </span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-2">
                      {c.fileId && (
                        <button type="button" disabled={busy !== null}
                          onClick={() => void certify('download', c.id)}
                          className="inline-flex items-center gap-1 text-erp-xs text-chocolate
                                     hover:underline">
                          <Download className="h-3 w-3" /> PDF
                        </button>
                      )}
                      {c.live && (
                        <button type="button" disabled={busy !== null}
                          onClick={() => void certify('revoke', c.id)}
                          className="inline-flex items-center gap-1 text-erp-xs
                                     text-red-700 dark:text-red-400 hover:underline">
                          <Ban className="h-3 w-3" /> Withdraw
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </ErpTable>
          )}
          <Note tone="info">
            Anyone can check a certificate at <strong>/verify</strong> using the reference
            printed on it — no account needed, which is the point. A withdrawn certificate
            still verifies, and says it was withdrawn rather than that it is a forgery.
          </Note>
        </div>
      )}
    </div>
  )
}

/**
 * What has to happen next, and where.
 *
 * Four acts in order, each gated on the last: close voting → count → award the ticket →
 * issue the certificate. Nothing here performs any of them; it only says which one is due,
 * so a page with no buttons on it stops looking like a page that is broken.
 */
function NextStep({ contest, hasResults, hasTicket, hasCertificate }: {
  contest: Nomination
  hasResults: boolean
  hasTicket: boolean
  hasCertificate: boolean
}) {
  const steps = [
    {
      done: contest.statusCode !== 'VOTING' && contest.statusCode !== 'ROLL_FROZEN',
      label: 'Close voting',
      where: 'on the Contests tab',
    },
    { done: hasResults, label: 'Count the ballots', where: 'the Count button above' },
    { done: hasTicket, label: 'Award the ticket', where: 'on the winning row below' },
    {
      done: hasCertificate,
      label: 'Issue the certificate',
      where: 'in the Certificates section below',
    },
  ]
  const next = steps.find((s) => !s.done)
  if (!next) {
    return (
      <Note tone="good">
        This contest is complete — counted, the ticket awarded, and the certificate issued.
      </Note>
    )
  }
  return (
    <Note tone="info">
      <span>
        <strong>Next: {next.label}</strong> — {next.where}.
        <span className="block text-erp-xs mt-1">
          {steps.map((s, i) => (
            <span key={s.label}>
              {i > 0 && ' → '}
              <span className={cn(s.done && 'line-through opacity-60',
                s === next && 'font-medium')}>{s.label}</span>
            </span>
          ))}
        </span>
      </span>
    </Note>
  )
}
